/**
 * Firebase Firestore Realtime Synchronization for Stellars Estates
 * Only initializes and connects if valid user Firebase credentials are provided in Admin Settings.
 */

const FIREBASE_CONFIG_KEY = 'stellars_firebase_custom_config_v1';

let dbInstance = null;
let isInitialized = false;

export function getStoredFirebaseConfig() {
  try {
    const custom = localStorage.getItem(FIREBASE_CONFIG_KEY);
    if (custom) {
      const parsed = JSON.parse(custom);
      if (parsed && parsed.projectId && parsed.apiKey && !parsed.apiKey.includes('demo')) {
        return parsed;
      }
    }
  } catch (e) {
    console.error('Error reading custom firebase config:', e);
  }
  return null;
}

export function saveStoredFirebaseConfig(config) {
  try {
    if (!config) {
      localStorage.removeItem(FIREBASE_CONFIG_KEY);
    } else {
      localStorage.setItem(FIREBASE_CONFIG_KEY, JSON.stringify(config));
    }
    // Reset instance so it re-initializes
    dbInstance = null;
    isInitialized = false;
  } catch (e) {
    console.error('Error saving custom firebase config:', e);
  }
}

export async function initFirestore() {
  if (dbInstance) return dbInstance;

  try {
    const config = getStoredFirebaseConfig();
    if (!config || !config.projectId || !config.apiKey) return null;

    const { initializeApp, getApps, getApp } = await import('firebase/app');
    const { getFirestore } = await import('firebase/firestore');

    const app = getApps().length > 0 ? getApp() : initializeApp(config);
    dbInstance = getFirestore(app);
    isInitialized = true;
    return dbInstance;
  } catch (e) {
    console.warn('Firestore initialization skipped:', e?.message || e);
    return null;
  }
}

/**
 * Subscribe to realtime updates from Firestore (if configured)
 */
export function subscribeToFirestore(onDataReceived, onError) {
  const config = getStoredFirebaseConfig();
  if (!config) return () => {};

  let unsubscribeFn = null;
  let isCancelled = false;

  (async () => {
    try {
      const db = await initFirestore();
      if (!db || isCancelled) return;

      const { doc, onSnapshot } = await import('firebase/firestore');
      const docRef = doc(db, 'stellars_config', 'custom_images');
      
      unsubscribeFn = onSnapshot(
        docRef,
        (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (data && typeof onDataReceived === 'function') {
              onDataReceived(data);
            }
          }
        },
        (error) => {
          console.warn('Firestore snapshot listener warning:', error?.message);
          if (typeof onError === 'function') onError(error);
        }
      );
    } catch (e) {
      console.warn('Firestore subscription failed:', e);
    }
  })();

  return () => {
    isCancelled = true;
    if (unsubscribeFn && typeof unsubscribeFn === 'function') {
      unsubscribeFn();
    }
  };
}

/**
 * Save images to Firestore (if configured)
 */
export async function saveToFirestore(imagesData) {
  const config = getStoredFirebaseConfig();
  if (!config) return false;

  try {
    const db = await initFirestore();
    if (!db) return false;

    const { doc, setDoc } = await import('firebase/firestore');
    const docRef = doc(db, 'stellars_config', 'custom_images');
    
    await setDoc(docRef, {
      ...imagesData,
      _syncedAt: new Date().toISOString(),
      _updatedTimestamp: Date.now()
    }, { merge: true });
    return true;
  } catch (e) {
    console.warn('Firestore save warning:', e?.message);
    return false;
  }
}
